import React from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import { getProfilePicture, getUserInitials, getRoleColor, getRoleIcon, getUserRole } from '../../utils/profileUtils';

const ProfileAvatar = ({ 
  user, 
  size = 'md', 
  showStatus = false, 
  status = 'online',
  className = '',
  onClick,
  showRole = false 
}) => {
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
    '2xl': 'w-20 h-20 text-2xl'
  };

  const statusColors = {
    online: 'bg-green-500',
    busy: 'bg-red-500',
    away: 'bg-yellow-500',
    offline: 'bg-gray-500'
  };

  const getRoleBorderColor = (role) => {
    switch (role) {
      case 'admin': return 'from-red-400/20 to-red-600/20 border-red-500/30';
      case 'writer': return 'from-green-400/20 to-green-600/20 border-green-500/30';
      case 'qa': return 'from-blue-400/20 to-blue-600/20 border-blue-500/30';
      case 'client': return 'from-purple-400/20 to-purple-600/20 border-purple-500/30';
      default: return 'from-primary/20 to-secondary/20 border-white/20';
    }
  };

  const userRole = getUserRole(user);
  const profilePicture = getProfilePicture(user);
  const initials = getUserInitials(user);

  return (
    <motion.div 
      className={`relative flex-shrink-0 ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
      whileHover={onClick ? { scale: 1.05 } : {}}
      whileTap={onClick ? { scale: 0.95 } : {}}
    >
      <div className={`
        ${sizeClasses[size]} 
        rounded-full 
        overflow-hidden 
        bg-gradient-to-br 
        ${getRoleBorderColor(userRole)}
        border-2
        flex 
        items-center 
        justify-center 
        relative
      `}>
        {profilePicture ? (
          <img
            src={profilePicture}
            alt={initials}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to initials if image fails to load
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {showRole ? (
              <Icon 
                name={getRoleIcon(userRole)} 
                size={size === 'xs' ? 12 : size === 'sm' ? 14 : size === 'md' ? 16 : size === 'lg' ? 20 : 24}
                className="text-white"
              />
            ) : (
              <span className="font-semibold text-white">
                {initials}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Status Indicator */}
      {showStatus && (
        <div className={`
          absolute -bottom-0.5 -right-0.5 
          ${size === 'xs' ? 'w-2 h-2' : size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3'} 
          ${statusColors[status]} 
          rounded-full 
          border-2 border-white/20
        `} />
      )}

      {/* Role Badge (optional) */}
      {showRole && userRole !== 'user' && (
        <div className={`
          absolute -top-1 -right-1 
          ${size === 'xs' ? 'w-4 h-4' : size === 'sm' ? 'w-5 h-5' : 'w-6 h-6'}
          bg-black/80 
          rounded-full 
          flex 
          items-center 
          justify-center 
          border border-white/20
        `}>
          <Icon 
            name={getRoleIcon(userRole)} 
            size={size === 'xs' ? 8 : size === 'sm' ? 10 : 12}
            className={getRoleColor(userRole)}
          />
        </div>
      )}
    </motion.div>
  );
};

export default ProfileAvatar;